import { useState } from 'react'

import { Trans, useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'

import CTA from 'components/CTA'
import { FilterPicker, Selection } from 'components/FilterPicker'
import type { MarketingDataType } from 'types'
import { TRENDING as TRENDING_SLUG } from 'libs/utils/categories'
import {
  Pen5038HeadingWrapper,
  useTrendingTitle,
  usePen5038Experiment,
} from 'experiments/pen-5038_v1_mega_content_picker'

import { CourseTileCarousel } from './CourseTileCarousel'
import { useCategories, useCourseTiles } from './dataFetching'
import { LOCATION_CAT_EXP, trackCategoryClick } from './events'

export type CatalogExplorerProps = {
  heading?: React.ReactNode
  openNewPage?: boolean
  showCTA?: boolean
  marketingRow?: MarketingDataType
}

export const CatalogExplorer = ({
  className,
  arialabelledBy = 'catalog-explorer-title',
  // pen-5038_v1 experiment
  marketingRow,
  ...props
}: { className?: string; arialabelledBy?: string } & CatalogExplorerProps) => {
  const { isEnabled: isPen5038Enabled } = usePen5038Experiment()
  const { marketingRows } = useStaticData<{
    marketingRows: MarketingDataType[]
  }>()

  const trendingRow =
    marketingRow && isPen5038Enabled
      ? marketingRow
      : marketingRows?.find((row) => row.slug === 'trending')

  if (!trendingRow) return <></>

  return (
    <section className={className} aria-labelledby={arialabelledBy}>
      <CatalogExplorerComp content={trendingRow} {...props} />
    </section>
  )
}

const CatalogExplorerComp = ({
  content,
  heading,
  openNewPage,
  showCTA = true,
}: { content: MarketingDataType } & CatalogExplorerProps) => {
  const { t } = useTranslation('@mc/next')
  const trendingTitle = useTrendingTitle(t('home.catalogExplorer.trending'))

  const categories = useCategories()
  const trendingItems = content.marketingSectionItems
  const [selectedCategorySlug, setSelectedCategorySlug] =
    useState<string>(TRENDING_SLUG)

  const { loading, error, courseTiles } = useCourseTiles({
    selectedCategorySlug,
    trendingItems,
  })

  const categorySelections: Array<Selection> = [
    ...[
      {
        slug: TRENDING_SLUG,
        name: trendingTitle,
      } as Selection,
    ],
    ...(categories.map((c) => ({
      name: c.name,
      slug: c.slug,
      id: c.id,
    })) as Array<Selection>),
  ]

  const selectedCategory = categories.find(
    (c) => selectedCategorySlug === c.slug,
  )

  return (
    <>
      {heading || (
        <Heading>
          <Pen5038HeadingWrapper
            control={
              <Trans t={t} i18nKey='home.catalogExplorer.headings.explore'>
                <br />
              </Trans>
            }
          />
        </Heading>
      )}
      <FilterPicker
        selections={categorySelections}
        selectedSlug={selectedCategorySlug}
        setSelectedSlug={setSelectedCategorySlug}
        onSelect={trackCategoryClick}
      />
      <CourseTileCarousel
        category={selectedCategory}
        loading={loading || error}
        courseTiles={courseTiles}
        openNewPage={openNewPage}
      />

      {showCTA && (
        <div className='mc-mt-8 mc-text--center'>
          <CTA
            kind='secondary'
            trackProps={{
              action: t('home.catalogExplorer.exploreCta'),
              content_type: 'product',
              location: LOCATION_CAT_EXP,
            }}
          >
            {t('home.catalogExplorer.exploreCta')}
          </CTA>
        </div>
      )}
    </>
  )
}

const Heading = ({ children: text }: { children: React.ReactNode }) => (
  <h2
    className='mc-mt-11 mc-mb-10 mc-text-h1 mc-text--center'
    id='catalog-explorer-title'
  >
    {text}
  </h2>
)
